import { Loader2, TrendingUp, DollarSign, MousePointerClick, Eye, BarChart3, Percent } from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";

const fmt = (n, decimals = 0) => {
  if (n == null) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toFixed(decimals);
};

const KPICard = ({ label, value, icon: Icon, suffix = "", color = "#ff3737", light = false }) => (
  <Card className={light ? "bg-white border-gray-200 shadow-sm" : "bg-[#1e2124] border-[#2a2d31] hover:border-[#3a3d41] transition-colors"}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-mono uppercase tracking-wider ${light ? "text-gray-400" : "text-[#ebebeb]/50"}`}>{label}</span>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
      </div>
      <p className={`text-xl font-bold font-mono ${light ? "text-gray-900" : "text-white"}`} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
        {value}{suffix}
      </p>
    </CardContent>
  </Card>
);

const MarketplaceKPIs = ({ summary, loading, revenueVisible = true, lightMode = false }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 text-[#ff3737] animate-spin" />
      </div>
    );
  }

  if (!summary || summary.row_count === 0) {
    return (
      <div className="text-center p-8">
        <BarChart3 className={`w-10 h-10 mx-auto mb-3 ${lightMode ? "text-gray-300" : "text-[#ebebeb]/20"}`} />
        <p className={`font-mono text-sm ${lightMode ? "text-gray-500" : "text-[#ebebeb]/50"}`}>No marketplace data yet. Upload SSP reports to get started.</p>
      </div>
    );
  }

  const s = summary;
  const cpc = s.total_clicks > 0 ? s.total_spend / s.total_clicks : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="marketplace-kpis">
      {revenueVisible && <KPICard label="Revenue" value={`$${fmt(s.total_revenue, 0)}`} icon={DollarSign} color="#22c55e" light={lightMode} />}
      <KPICard label="Spend" value={`$${fmt(s.total_spend, 0)}`} icon={TrendingUp} color="#3b82f6" light={lightMode} />
      {revenueVisible && <KPICard label="Margin" value={s.margin_pct?.toFixed(1)} suffix="%" icon={Percent} color="#f59e0b" light={lightMode} />}
      <KPICard label="Impressions" value={fmt(s.total_impressions)} icon={Eye} color="#8b5cf6" light={lightMode} />
      <KPICard label="Clicks" value={fmt(s.total_clicks)} icon={MousePointerClick} color="#ec4899" light={lightMode} />
      <KPICard label="CPM" value={`$${s.cpm?.toFixed(2)}`} icon={BarChart3} color="#06b6d4" light={lightMode} />
      <KPICard label="CPC" value={`$${cpc.toFixed(2)}`} icon={DollarSign} color="#f97316" light={lightMode} />
      <KPICard label="CTR" value={s.ctr?.toFixed(2)} suffix="%" icon={TrendingUp} color="#14b8a6" light={lightMode} />
    </div>
  );
};

export default MarketplaceKPIs;
